import { useCallback, useEffect, useState } from 'react';
import { useAuth } from '@workos-inc/authkit-react';
import { logger, setPendingReturnTo } from '~portal/lib';

type SignInRedirect = {
  /** True once we know the redirect cannot happen; render a retry instead of a loader. */
  failed: boolean;
  /** Clear the failure and attempt the redirect again. */
  retry: () => void;
};

/**
 * @desc Drive the AuthKit sign-in redirect and surface it when the redirect
 * cannot happen, instead of leaving the caller on a loader forever.
 *
 * `signIn()` cannot fail visibly. When the SDK fails to initialise — which it
 * does in browsers that block cookies and site data, because AuthKit reads
 * `sessionStorage` outside its own error handling — the provider installs a
 * no-op client whose `signIn` resolves without navigating anywhere. In the same
 * browsers the real client's `signIn` rejects while writing its PKCE verifier,
 * into a promise the caller has already discarded. Both end as a spinner that
 * never resolves.
 *
 * `getSignInUrl` distinguishes both cases in one call: the no-op client returns
 * an empty string and the real client rejects. So we ask for the URL, navigate
 * ourselves, and treat anything else as a failure.
 */
export function useSignInRedirect(enabled: boolean, returnTo?: string): SignInRedirect {
  const { getSignInUrl } = useAuth();
  const [failed, setFailed] = useState(false);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    if (!enabled) return;

    let cancelled = false;

    getSignInUrl(returnTo ? { state: { returnTo } } : {})
      .then((url) => {
        if (cancelled) return;

        if (!url) {
          logger.error('sign-in unavailable: AuthKit failed to initialise', { returnTo });
          setFailed(true);
          return;
        }

        // Park the deep link before leaving: if the roundtrip's own state echo is lost or a
        // retry fires from '/', the protected layout still restores this original target.
        if (returnTo && returnTo !== '/') setPendingReturnTo(returnTo);
        window.location.assign(url);
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        logger.error('sign-in redirect failed', { error, returnTo });
        setFailed(true);
      });

    return () => {
      cancelled = true;
    };
  }, [enabled, getSignInUrl, returnTo, attempt]);

  const retry = useCallback(() => {
    setFailed(false);
    setAttempt((n) => n + 1);
  }, []);

  return { failed, retry };
}
